import { Fragment } from 'react'
import { Link } from 'react-router-dom'
import type { NavGroupDef, NavItemDef } from '../../../navigation/menu.ts'
import { Icon } from '../Icon'
import './nav.css'

/**
 * Voce di menu. NON usa NavLink: la voce attiva è decisa una volta sola da `resolveActive`
 * (prefisso più lungo, query string per le voci di Operazioni — TASK-0062) e arriva qui come `active`.
 * Con NavLink più voci potevano accendersi insieme (es. /contracts/create accendeva anche Contratti).
 */
export function NavItemLink({ item, active, onNavigate, trailing }: {
  item: NavItemDef
  active: boolean
  onNavigate?: (() => void) | undefined
  trailing?: string | undefined
}) {
  return (
    <Link
      to={item.to}
      className={'md-nav-item md-state' + (active ? ' is-active' : '')}
      aria-current={active ? 'page' : undefined}
      onClick={onNavigate}
    >
      <Icon name={item.icon} size={24} filled={active} />
      <span className="md-nav-item__label">{item.label}</span>
      {trailing && <span className="md-nav-item__trailing">{trailing}</span>}
    </Link>
  )
}

/**
 * Gruppo a tendina del menu (pattern "disclosure"): il bottone di testata controlla il pannello,
 * che da chiuso è `inert` + nascosto, così i suoi link non sono raggiungibili con Tab.
 */
export function NavGroup({ group, open, hasActive, activeItemId, onToggle, onNavigate }: {
  group: NavGroupDef
  open: boolean
  hasActive: boolean
  activeItemId: string | null
  onToggle: () => void
  onNavigate?: (() => void) | undefined
}) {
  const headerId = `nav-gh-${group.id}`
  const panelId = `nav-g-${group.id}`
  return (
    <div className="md-nav-group" data-open={open} data-has-active={hasActive}>
      <button
        type="button"
        id={headerId}
        className="md-nav-group__header md-state"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={onToggle}
      >
        <Icon name={group.icon} size={24} filled={hasActive} />
        <span className="md-nav-group__title">{group.label}</span>
        {hasActive && !open && <span className="md-nav-group__dot" aria-hidden="true" />}
        <span className="md-nav-group__chevron"><Icon name="expand_more" size={24} /></span>
      </button>
      <div className="md-nav-group__panel">
        <div className="md-nav-group__inner" role="group" id={panelId} aria-labelledby={headerId} inert={!open}>
          <ul className="md-nav-list">
            {group.items.map((item, i) => {
              const prev = group.items[i - 1]
              const newSection = item.section !== undefined && item.section !== prev?.section
              return (
                <Fragment key={item.id}>
                  {newSection && (
                    <li role="presentation" className="md-nav-section">
                      <span className="md-nav-section__rule" aria-hidden="true" />
                      <span className="md-nav-section__title">{item.section}</span>
                    </li>
                  )}
                  <li>
                    <NavItemLink item={item} active={item.id === activeItemId} onNavigate={onNavigate} />
                  </li>
                </Fragment>
              )
            })}
          </ul>
        </div>
      </div>
    </div>
  )
}
